'use client';

import React, { useLayoutEffect, useRef } from 'react';
import { useGSAP } from '@gsap/react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

/**
 * Hand-drawn SVG strokes that draw themselves (on mount, when scrolled into view, or scrubbed by scroll).
 *
 * Paths are authored in a small design box and rescaled into *pixel space* at runtime (viewBox == rendered box),
 * so there is no non-uniform SVG scaling: stroke widths stay crisp in px and dash lengths can be measured
 * exactly with getTotalLength(). Paths may exceed the design box (overflow is visible) so loops wrap loosely
 * around the element they decorate.
 */
export type ScribbleKind = 'underline' | 'converge' | 'circle' | 'strike' | 'arrow' | 'zigzag' | 'burst' | 'cross' | 'bracket';

const PATHS: Record<ScribbleKind, { d: string[]; box: [number, number]; stagger?: number }> = {
  underline: {
    box: [300, 40],
    d: ['M4 22 C 60 6, 120 34, 180 18 S 260 8, 296 20', 'M10 30 C 80 18, 150 40, 220 26 S 270 20, 292 30'],
  },
  // three strokes arriving from different directions and settling into one underline
  converge: {
    box: [300, 40],
    stagger: 0,
    d: [
      'M-10 -8 C 30 12, 90 26, 150 22 S 260 14, 298 20',
      'M310 -6 C 270 10, 210 30, 150 28 S 50 24, 2 26',
      'M4 42 C 60 34, 130 30, 200 26 S 270 20, 298 20',
    ],
  },
  circle: {
    box: [300, 100],
    d: [
      'M160 -8 C 60 -12, -22 20, -14 56 C -6 92, 80 112, 170 106 C 250 100, 322 76, 314 42 C 306 6, 230 -10, 150 -6',
      'M140 -4 C 40 4, -30 34, -8 66 C 8 88, 70 104, 150 102',
    ],
  },
  strike: {
    box: [300, 60],
    d: ['M6 32 C 70 18, 140 40, 210 26 S 270 18, 296 28', 'M8 26 C 90 44, 160 14, 294 34'],
  },
  arrow: {
    box: [200, 200],
    d: ['M12 20 C 40 90, 60 150, 150 172', 'M127.7 146.4 L 150 172 L 118.3 184.2'],
  },
  zigzag: {
    box: [300, 40],
    d: ['M4 20 L 30 6 L 56 34 L 82 6 L 108 34 L 134 6 L 160 34 L 186 6 L 212 34 L 238 6 L 264 34 L 296 10'],
  },
  burst: {
    box: [100, 100],
    stagger: 0.04,
    d: ['M50 28 L 50 4', 'M70 34 L 88 16', 'M30 34 L 12 16', 'M76 54 L 98 54', 'M24 54 L 2 54', 'M66 74 L 82 90', 'M34 74 L 18 90'],
  },
  cross: {
    box: [100, 100],
    d: ['M8 10 C 30 30, 60 70, 92 92', 'M92 8 C 70 32, 40 60, 8 90'],
  },
  bracket: {
    box: [40, 300],
    d: ['M34 4 C 8 20, 4 60, 8 150 C 12 240, 4 280, 36 296'],
  },
};

/** Scale absolute-coordinate path data (M/L/C/S only, which is all we author) by sx/sy. */
function scalePath(d: string, sx: number, sy: number): string {
  let i = 0;
  return d.replace(/-?\d*\.?\d+/g, (n) => {
    const v = parseFloat(n) * (i++ % 2 === 0 ? sx : sy);
    return v.toFixed(2);
  });
}

interface Props extends React.SVGAttributes<SVGSVGElement>
{
  kind: ScribbleKind;
  color?: string;
  width?: number;      // stroke width in px
  delay?: number;
  duration?: number;
  trigger?: 'view' | 'mount' | 'scrub';
  start?: string;      // ScrollTrigger start for view/scrub
  end?: string;        // ScrollTrigger end for scrub
}

export default function Scribble({ kind, color = 'var(--sup-heat)', width = 6, delay = 0, duration = 0.9, trigger = 'view', start = 'top 85%', end = 'top 40%', className = '', style, ...rest }: Props) {
  const ref = useRef<SVGSVGElement>(null);
  const progress = useRef({ p: 0 });
  const lengths = useRef<number[]>([]);
  const { d, box, stagger } = PATHS[kind];

  // per-path time windows inside the 0..1 progress (emulates GSAP stagger)
  const n = d.length;
  const each = stagger ?? duration / n;
  const per = stagger !== undefined ? duration : duration / n;
  const total = Math.max(0.0001, each * (n - 1) + per);
  const windows = d.map((_, i) => [ (i * each) / total, (i * each + per) / total ] as const);

  const paint = () => {
    const svg = ref.current; if (!svg) return;
    const p = progress.current.p;
    svg.querySelectorAll('path').forEach((path, i) => {
      const len = lengths.current[i] ?? 0;
      const [a, b] = windows[i];
      const local = Math.min(1, Math.max(0, (p - a) / (b - a)));
      path.style.strokeDashoffset = `${ len * (1 - local) }`;
    });
  };

  // fit geometry to the rendered box; re-measure on resize
  useLayoutEffect(() => {
    const svg = ref.current; if (!svg) return;
    const fit = () => {
      const { width: w, height: h } = svg.getBoundingClientRect();
      if (!w || !h) return;
      svg.setAttribute('viewBox', `0 0 ${ w } ${ h }`);
      const sx = w / box[0], sy = h / box[1];
      svg.querySelectorAll('path').forEach((path, i) => {
        path.setAttribute('d', scalePath(d[i], sx, sy));
        const len = path.getTotalLength();
        lengths.current[i] = len;
        path.style.strokeDasharray = `${ len }`;
      });
      paint();
    };
    fit();
    const ro = new ResizeObserver(fit);
    ro.observe(svg);
    return () => ro.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kind]);

  useGSAP(() => {
    gsap.registerPlugin(ScrollTrigger);
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced) { progress.current.p = 1; paint(); return; }
    if (trigger === 'scrub') {
      gsap.to(progress.current, { p: 1, ease: 'none', onUpdate: paint, scrollTrigger: { trigger: ref.current, start, end, scrub: 0.4 } });
      return;
    }
    gsap.to(progress.current, {
      p: 1, duration: total, ease: 'power2.inOut', delay, onUpdate: paint,
      scrollTrigger: trigger === 'view' ? { trigger: ref.current, start, once: true } : undefined,
    });
  }, { scope: ref });

  // A replaced element with `left` + `right` keeps its intrinsic aspect ratio instead of stretching,
  // so turn the pair into an explicit width.
  const { left, right, ...restStyle } = style ?? {};
  const stretch = left !== undefined && right !== undefined;
  const boxStyle: React.CSSProperties = {
    ...restStyle,
    left,
    right: stretch ? undefined : right,
    width: stretch ? `calc(100% - (${ left }) - (${ right }))` : restStyle.width,
  };

  return (
    <svg ref={ ref } viewBox={ `0 0 ${ box[0] } ${ box[1] }` } preserveAspectRatio='none' aria-hidden='true' className={ `sup-scribble ${ className }` } style={ boxStyle } { ...rest }>
      { d.map((p, i) => <path key={ i } d={ p } stroke={ color } strokeWidth={ width } style={ { strokeDasharray: 99999, strokeDashoffset: 99999 } } />) }
    </svg>
  );
}
